/**
 * `ExplorationSkillEditor` — selezione delle skill cercate da un'esplorazione,
 * ciascuna con il **Livello minimo richiesto** (obbligatorio).
 *
 * Ricalca {@link SkillWeightEditor} ma senza la nozione di peso: nella sezione
 * "Explore" tutte le skill cercate contano allo stesso modo. Combina il
 * {@link ChipAutocomplete} (ricerca + aggiunta/rimozione skill) con una
 * tabellina che per ogni skill selezionata espone un {@link LivelloSelect} per
 * il Livello richiesto.
 *
 * Il componente è controllato: lo stato (`selectedIds` + `requiredLevels`) vive
 * nel genitore. A differenza dell'editor Competenza, qui il Livello è
 * **obbligatorio** (nessun "qualsiasi livello"): il `LivelloSelect` non è
 * azzerabile e il genitore considera "complete" solo le skill con un Livello.
 */
import { useMemo } from 'react';
import type { Livello } from '../shared/dto';
import { LivelloGuide } from './LivelloGuide';
import { LivelloSelect } from './LivelloSelect';
import { ChipAutocomplete, type ChipOption } from './ui/ChipAutocomplete';

interface ExplorationSkillEditorProps {
  /** `id` del controllo di ricerca (collegato alla `<label>` del `Field`). */
  id: string;
  /** Elenco completo delle skill disponibili. */
  options: readonly ChipOption[];
  /** `id` delle skill attualmente selezionate. */
  selectedIds: readonly string[];
  /** Livello richiesto corrente per skill id (assente finché non scelto). */
  requiredLevels: Record<string, Livello>;
  /** Notifica il nuovo insieme di skill selezionate. */
  onChangeIds: (next: string[]) => void;
  /** Notifica la nuova mappa completa dei Livelli richiesti. */
  onChangeRequiredLevels: (next: Record<string, Livello>) => void;
  placeholder?: string;
  emptyMessage?: string;
}

export function ExplorationSkillEditor({
  id,
  options,
  selectedIds,
  requiredLevels,
  onChangeIds,
  onChangeRequiredLevels,
  placeholder = 'Cerca una skill…',
  emptyMessage = 'Nessuna skill corrisponde.',
}: ExplorationSkillEditorProps) {
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) map.set(o.id, o.name);
    return map;
  }, [options]);

  // Alla rimozione di una skill ne scartiamo anche l'eventuale Livello, così la
  // mappa non trattiene voci orfane.
  const handleIdsChange = (next: string[]) => {
    const nextSet = new Set(next);
    const prunedLevels: Record<string, Livello> = {};
    for (const [skillId, level] of Object.entries(requiredLevels)) {
      if (nextSet.has(skillId)) prunedLevels[skillId] = level;
    }
    onChangeIds(next);
    onChangeRequiredLevels(prunedLevels);
  };

  const setRequiredLevel = (skillId: string, level: Livello) => {
    onChangeRequiredLevels({ ...requiredLevels, [skillId]: level });
  };

  return (
    <div className="skill-weight-editor">
      <ChipAutocomplete
        id={id}
        options={options}
        selectedIds={selectedIds}
        onChange={handleIdsChange}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
      />

      {selectedIds.length > 0 && (
        <table className="skill-weight-editor__table">
          <caption className="sr-only">
            Livello minimo richiesto per ciascuna skill cercata
          </caption>
          <thead>
            <tr>
              <th scope="col">Skill</th>
              <th scope="col" className="skill-weight-editor__level-col">
                <span className="skill-weight-editor__level-head">
                  Livello richiesto
                  <LivelloGuide />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {selectedIds.map((skillId) => {
              const requiredLevel = requiredLevels[skillId] ?? null;
              const skillLabel = nameById.get(skillId) ?? skillId;
              return (
                <tr key={skillId}>
                  <td>
                    <span className="skill-weight-editor__skill-label">
                      {skillLabel}
                    </span>
                  </td>
                  <td className="skill-weight-editor__level-col">
                    <span
                      className="user-admin__select skill-weight-editor__level-select"
                      data-empty={requiredLevel === null || undefined}
                    >
                      {/* Livello obbligatorio: nessun `onClear` (non azzerabile). */}
                      <LivelloSelect
                        value={requiredLevel}
                        onChange={(next) => setRequiredLevel(skillId, next)}
                        label={`Livello richiesto di ${skillLabel}`}
                        placeholder="Seleziona un livello…"
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ExplorationSkillEditor;
